import { type QueryClient } from "@tanstack/react-query";
import type { ReactElement } from "react";
import {
  Route,
  redirect,
  type LoaderFunction,
  type LoaderFunctionArgs,
} from "react-router";

import { queryClient as defaultQueryClient } from "@/providers/QueryClient";
import { orgStorage } from "@/shared/auth";
import { type EventModel, OrgService } from "@/shared/domain/orgs";
import { orgQueryKeys } from "@/shared/domain/orgs/hooks/orgQueryKeys";
import { ORG_STALE_TIME_MS } from "@/shared/domain/orgs/hooks/useOrg";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";

import { composeLoaders } from "./composeLoaders";

interface OrgEventModelRouteOptions {
  path: string;
  element: ReactElement;
  /**
   * Whether an org with this event model may open the route, such as
   * `hasSessionEvents`. Called with `undefined` when the org record cannot be
   * fetched.
   */
  allows: (model: EventModel | undefined) => boolean;
  redirectTo: string | ((args: LoaderFunctionArgs) => string);
  /**
   * The org that owns the route's resource, such as a session's org; the gate
   * checks that org's event model. When omitted, or when it resolves to
   * `undefined` because the resource cannot be fetched, the gate checks the
   * current org's. A user can open a resource of an org other than the
   * selected one, and the resource's events live in its own org's stores.
   */
  owningOrgId?: (
    args: LoaderFunctionArgs,
    queryClient: QueryClient,
  ) => Promise<string | undefined>;
  /**
   * The route's own loader, run alongside the gate (see `composeLoaders`):
   * the gate's redirect wins, so a disallowed org never mounts the element.
   */
  loader?: LoaderFunction;
}

/**
 * Factory for a `<Route>` that renders `element` only for an org whose event
 * model `allows` it, and otherwise redirects to `redirectTo` before the
 * element mounts. The event-model counterpart of `featureFlagRoute`, gating
 * in the loader for the same reason: a client-side redirect inside the
 * element would flash it on a cold load.
 *
 * Fetches the org record under the key `useOrg` reads, so the route gate and
 * `useOrgEventModel` in the page agree and share one request.
 *
 * - No current org in storage: renders `element`, deferring to the
 *   authentication layout, as `featureFlagRoute` does.
 * - The event model passes `allows`: renders `element`. The model is that of
 *   the org `owningOrgId` resolves, or of the current org when it resolves none.
 * - Otherwise, including a failed fetch of the org record: redirects to
 *   `redirectTo`.
 */
export function orgEventModelRoute({
  path,
  allows,
  redirectTo,
  owningOrgId,
  element,
  loader,
}: OrgEventModelRouteOptions): ReactElement {
  const gate: LoaderFunction = async (args) => {
    const currentOrg = orgStorage.getCurrentOrg();
    if (!currentOrg) {
      return null;
    }
    const orgId =
      (await owningOrgId?.(args, defaultQueryClient)) ?? currentOrg.org_id;

    let model: EventModel | undefined;
    try {
      const org = await defaultQueryClient.fetchQuery({
        queryKey: orgQueryKeys.org(orgId),
        queryFn: async ({ signal }) => {
          const httpClient = await makeAuthenticatedHttpClient();
          return new OrgService(httpClient).getOrg(orgId, {
            abortSignal: signal,
          });
        },
        staleTime: ORG_STALE_TIME_MS,
      });
      model = org.event_model ?? "legacy";
    } catch {
      model = undefined;
    }

    if (allows(model)) {
      return null;
    }
    return redirect(
      typeof redirectTo === "function" ? redirectTo(args) : redirectTo,
    );
  };
  return (
    <Route
      key={path}
      path={path}
      loader={loader ? composeLoaders(gate, loader) : gate}
      element={element}
    />
  );
}
